import React, { Component } from 'react';
import { Container, Row, Col, Card, Modal, Form, Alert } from 'react-bootstrap';
import { withRouter } from 'react-router-dom';
import { withApollo } from 'react-apollo';
import gql from 'graphql-tag';
import AdmissionTestExam from './AdmissionTestExam';
import './AdmissionTest.scss';

const VERIFY_ADMISSION_TEST_LOGIN = gql`
  query($username: String!, $password: String!) {
    verifyAdmissionTestLogin(username: $username, password: $password) {
      id
      enquiry_form_master_id
      admission_test_id
      username
      password
      branch_name
      course_name
      estatus
    }
  }
`;

const GET_ADMISSION_TEST_QUESTIONS_COUNT = gql`
  query($admission_test_id: Int!) {
    getAdmissionTestQuestions(admission_test_id: $admission_test_id) {
      id
    }
  }
`;

const GET_ADMISSION_TEST_SESSION = gql`
  query($enquiry_form_master_id: Int!, $admission_test_id: Int!) {
    getAdmissionTestSession(
      enquiry_form_master_id: $enquiry_form_master_id
      admission_test_id: $admission_test_id
    ) {
      id
      is_completed
    }
  }
`;

const GET_ADMISSION_TEST_PATTERN = gql`
  query($admission_test_id: Int!) {
    getAdmissionTestPattern(admission_test_id: $admission_test_id) {
      duration
      marks
      instructions {
        instructions
      }
    }
  }
`;

// ─── Constants ──────────────────────────────────────────────────────────────
const VIEWS = { LANDING: 'landing', INSTRUCTIONS: 'instructions', EXAM: 'exam' };


const MARKING_SCHEME = [
  { label: 'Correct Answer', value: '+4', mod: 'correct' },
  { label: 'Wrong Answer', value: '-1', mod: 'wrong' },
  { label: 'Unanswered', value: '0', mod: 'skip' },
];

const INSTRUCTIONS = [
  { icon: '🔒', text: 'Do not refresh or close the browser during the exam' },
  { icon: '🚫', text: 'Once submitted, you cannot retake the exam' },
  { icon: '🔢', text: 'Use the Question Navigator panel to jump between questions' },
  { icon: '⏱', text: 'Exam duration is 180 minutes. Timer starts as soon as you click Start Exam' },
  { icon: '📝', text: 'There are 3 types of questions: Options, One word, and Matching options only' },
  { icon: '📊', text: 'Your result will be shown immediately after submission' },
];

// ─── Helper: read ?view= from URL ────────────────────────────────────────────
function getViewFromSearch(search) {
  const params = new URLSearchParams(search);
  return params.get('view') || VIEWS.LANDING;
}

// ════════════════════════════════════════════════════════════════════════════════
// Main Component
// ════════════════════════════════════════════════════════════════════════════════
class AdmissionTestHome extends Component {
  constructor(props) {
    super(props);
    this.state = {
      // Flow state
      currentView: getViewFromSearch(props.location.search),
      session: null,
      showLoginModal: false,

      // Login form
      username: '', password: '', loginError: '', loginLoading: false,
    };
  }

  // ─── Navigation helpers ────────────────────────────────────────────────────
  setView(view) {
    const { history, location } = this.props;
    const params = new URLSearchParams(location.search);
    if (view === VIEWS.LANDING) params.delete('view');
    else params.set('view', view);
    history.replace({ pathname: location.pathname, search: params.toString() });
    this.setState({ currentView: view });
  }

  // ─── Login Logic ──────────────────────────────────────────────────────────
  handleLogin = async () => {
    const { username, password } = this.state;
    if (!username || !password) {
      this.setState({ loginError: 'Please enter username and password' });
      return;
    }
    this.setState({ loginLoading: true, loginError: '' });
    try {
      const result = await this.props.client.query({
        query: VERIFY_ADMISSION_TEST_LOGIN,
        variables: { username, password },
        fetchPolicy: 'network-only',
      });
      const record = result.data.verifyAdmissionTestLogin;
      if (record) {
        // Check if session is already completed
        try {
          const sessionResult = await this.props.client.query({
            query: GET_ADMISSION_TEST_SESSION,
            variables: { 
              enquiry_form_master_id: parseInt(record.enquiry_form_master_id),
              admission_test_id: parseInt(record.admission_test_id)
            },
            fetchPolicy: 'network-only',
          });
          
          if (sessionResult.data.getAdmissionTestSession && sessionResult.data.getAdmissionTestSession.is_completed === 1) {
            // Already completed, redirect to result screen
            this.setState({ showLoginModal: false, loginLoading: false, username: '', password: '' });
            this.props.history.push("/admission-test-result");
            return;
          }
        } catch (e) {
          console.error("Failed to fetch session status", e);
        }

        let total_questions = '--';
        try {
          const qResult = await this.props.client.query({
            query: GET_ADMISSION_TEST_QUESTIONS_COUNT,
            variables: { admission_test_id: parseInt(record.admission_test_id) },
            fetchPolicy: 'network-only',
          });
          const questions = qResult.data.getAdmissionTestQuestions;
          if (questions && Array.isArray(questions)) {
            total_questions = questions.length;
          }
        } catch (e) {
          console.error('Failed to fetch questions count', e);
        }

        let exam_duration = 180;
        let exam_marks = '--';
        let exam_instructions = null;
        try {
          const pResult = await this.props.client.query({
            query: GET_ADMISSION_TEST_PATTERN,
            variables: { admission_test_id: parseInt(record.admission_test_id) },
            fetchPolicy: 'network-only',
          });
          const pattern = pResult.data.getAdmissionTestPattern;
          if (pattern) {
            if (pattern.duration) exam_duration = parseInt(pattern.duration);
            if (pattern.marks) exam_marks = pattern.marks;
            if (pattern.instructions && pattern.instructions.instructions) {
               try {
                  exam_instructions = decodeURIComponent(pattern.instructions.instructions);
               } catch(e) {
                  exam_instructions = pattern.instructions.instructions; // fallback
               }
            }
          }
        } catch (e) {
          console.error('Failed to fetch pattern', e);
        }

        const session = {
          enquiry_form_master_id: record.enquiry_form_master_id,
          admission_test_id: record.admission_test_id,
          username: record.username,
          student_name: record.username,
          branch_name: record.branch_name,
          course_name: record.course_name,
          total_questions,
          exam_duration,
          exam_marks,
          exam_instructions
        };
        localStorage.setItem('admissionGuest', JSON.stringify(session));
        this.setState({ session, showLoginModal: false, loginLoading: false, username: '', password: '' });
        this.setView(VIEWS.INSTRUCTIONS);
      }
    } catch (err) {
      this.setState({
        loginError: err.message?.replace('GraphQL error: ', '') || 'Invalid username or password',
        loginLoading: false,
      });
    }
  };

  handleCloseModal = () =>
    this.setState({ showLoginModal: false, loginError: '', username: '', password: '' });

  // ─── Start Exam ────────────────────────────────────────────────────────────
  startExam = () => {
    this.setView(VIEWS.EXAM);
  };

  // ══════════════════════════════════════════════════════════════════════════
  // RENDER: Landing
  // ══════════════════════════════════════════════════════════════════════════
  renderLanding() {
    return (
      <div className="at-landing">
        {/* Header */}
        <div className="at-landing__header">
          <div className="at-landing__container at-landing__header-inner">
            <span className="at-landing__logo">Rizee</span>
            <button className="at-landing__nav-login-btn" onClick={() => this.setState({ showLoginModal: true })}>
              Login
            </button>
          </div>
        </div>

        {/* Hero */}
        <div className="at-landing__hero">
          <div className="at-landing__container at-landing__hero-inner">
            <div className="at-landing__hero-content">
              <span className="at-landing__badge">Online Assessment</span>
              <h1 className="at-landing__title">Welcome to the<br />Rizee Admission Test</h1>
              <p className="at-landing__subtitle">
                A secure, comprehensive, and time-bound evaluation designed to recognise your potential.
                Please have your login credentials ready.
              </p>
              <button className="at-landing__start-btn" onClick={() => this.setState({ showLoginModal: true })}>
                Start Admission Test
              </button>
            </div>
            <div className="at-landing__hero-visual">
              <div className="at-landing__visual-circle">🎓</div>
            </div>
          </div>
        </div>

        {/* Features */}
        <div className="at-landing__features">
          <div className="at-landing__container">
            <h2 className="at-landing__features-title">Assessment Features</h2>
            <div className="at-landing__features-grid">
              {[
                { icon: '📝', title: 'Multiple Choice', desc: 'Carefully curated objective questions.' },
                { icon: '⏱', title: 'Time-Limited', desc: 'Structured duration for fair assessment.' },
                { icon: '🔒', title: 'Proctored & Secure', desc: 'Secure environment ensuring integrity.' },
                { icon: '📊', title: 'Instant Results', desc: 'Immediate automated evaluation.' },
              ].map((item, i) => (
                <div key={i} className="at-landing__feature-card">
                  <div className="at-landing__feature-icon">{item.icon}</div>
                  <h3 className="at-landing__feature-name">{item.title}</h3>
                  <p className="at-landing__feature-desc">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // RENDER: Instructions
  // ══════════════════════════════════════════════════════════════════════════
  renderInstructions() {
    let session = this.state.session;
    if (!session) {
      const stored = localStorage.getItem('admissionGuest');
      if (stored) {
        try { session = JSON.parse(stored); } catch (e) { }
      }
    }
    session = session || {
      student_name: 'Student', admission_test: 'Admission Test',
      total_questions: '--', duration_minutes: 30,
    };
    return (
      <div className="at-landing">
        {/* Header — same as landing */}
        <div className="at-landing__header">
          <div className="at-landing__container at-landing__header-inner">
            <span className="at-landing__logo">Rizee</span>
            <span className="at-landing__header-label">Admission Test</span>
          </div>
        </div>

        <div className="at-landing__instructions-page">
          <div className="at-landing__container">

            <div className="at-instr__grid">

              {/* LEFT COLUMN: Student Banner + Marking Scheme */}
              <div className="at-instr__grid-left">
                {/* Student Info Banner */}
                <div className="at-instr__student-banner">
                  <div className="at-instr__student-avatar">🎓</div>
                  <div className="at-instr__student-info">
                    <h2 className="at-instr__student-name">{session.student_name}</h2>
                    <p className="at-instr__student-exam">{session.admission_test}</p>
                  </div>
                  <div className="at-instr__stats">
                    {[
                      { val: session.total_questions || '--', label: 'Questions' },
                      { val: session.exam_duration || 180, label: 'Mins' },
                      { val: session.exam_marks || '--', label: 'Marks' },
                    ].map((item, i) => (
                      <div key={i} className="at-instr__stat-item">
                        <div className="at-instr__stat-val">{item.val}</div>
                        <div className="at-instr__stat-label">{item.label}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Marking Scheme */}
                <div className="at-instr__card">
                  <div className="at-instr__card-header">
                    <span className="at-instr__card-icon">📋</span>
                    <h3 className="at-instr__card-title">Marking Scheme</h3>
                  </div>
                  <div className="at-instr__scheme-row">
                    {MARKING_SCHEME.map((item, i) => (
                      <div key={i} className={`at-instr__scheme-box at-instr__scheme-box--${item.mod}`}>
                        <div className="at-instr__scheme-val">{item.value}</div>
                        <div className="at-instr__scheme-text">{item.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Instructions list */}
              <div className="at-instr__card">
                <div className="at-instr__card-header">
                  <span className="at-instr__card-icon">📌</span>
                  <h3 className="at-instr__card-title">Important Guidelines</h3>
                </div>
                <div className="at-instr__list">
                  {session.exam_instructions ? (
                    <div className="p-3" dangerouslySetInnerHTML={{ __html: session.exam_instructions }} />
                  ) : (
                    INSTRUCTIONS.map((item, idx) => (
                      <div key={idx} className="at-instr__list-item">
                        <span className="at-instr__list-icon">{item.icon}</span>
                        <span className="at-instr__list-text">{item.text}</span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* Start Button */}
            <div className="at-instr__footer">
              <button className="at-instr__btn-start" onClick={this.startExam}>
                ✅ I Understand, Start Exam
              </button>
            </div>

          </div>
        </div>
      </div>
    );
  }



  // ══════════════════════════════════════════════════════════════════════════
  // LOGIN MODAL (shown on landing)
  // ══════════════════════════════════════════════════════════════════════════
  renderLoginModal() {
    const { showLoginModal, username, password, loginError, loginLoading } = this.state;
    return (
      <Modal show={showLoginModal} onHide={this.handleCloseModal} centered dialogClassName="at-modal">
        <Modal.Header closeButton>
          <Modal.Title>🎓 Admission Test Login</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          {loginError && <Alert variant="danger" className="py-2">{loginError}</Alert>}
          <Form.Group className="mb-4">
            <label className="at-modal__label">Username</label>
            <input type="text" className="at-modal__input" placeholder="Enter username"
              value={username} onChange={e => this.setState({ username: e.target.value })} />
          </Form.Group>
          <Form.Group className="mb-2">
            <label className="at-modal__label">Password</label>
            <input type="password" className="at-modal__input" placeholder="Enter password"
              value={password}
              onChange={e => this.setState({ password: e.target.value })}
              onKeyPress={e => e.key === 'Enter' && this.handleLogin()} />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer className="flex-column border-top-0 pt-0 pb-4">
          <div className="d-flex justify-content-center w-100 mb-2">
            <button className="at-modal__btn-cancel mr-3" onClick={this.handleCloseModal}>Cancel</button>
            <button className="at-modal__btn-login" onClick={this.handleLogin} disabled={loginLoading}>
              {loginLoading ? 'Verifying...' : 'Login & Continue'}
            </button>
          </div>
          <div className="text-center w-100 mt-2">
            <span style={{ fontSize: '13px', color: '#64748b' }}>Having trouble? </span>
            <a href="#" style={{ fontSize: '13px', fontWeight: '600', textDecoration: 'underline', color: '#0f5132' }}>Contact Support</a>
          </div>
        </Modal.Footer>
      </Modal>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // MAIN RENDER
  // ══════════════════════════════════════════════════════════════════════════
  render() {
    const { currentView } = this.state;
    return (
      <>
        {currentView === VIEWS.LANDING && this.renderLanding()}
        {currentView === VIEWS.INSTRUCTIONS && this.renderInstructions()}
        {currentView === VIEWS.EXAM && <AdmissionTestExam skipInstructions />}
        {this.renderLoginModal()}
      </>
    );
  }
}

export default withRouter(withApollo(AdmissionTestHome));
